import { TextArea } from "@interstate/components/TextArea";
import { replyInputTestId } from "../../testIds";
import { TextInputEventValue } from "@interstate/components/TextInput";
import { InterstateOnChangeEvent } from "@interstate/components/InterstateEvents";
import { StationeryOptions } from "@coxauto-ui/cxm/communications/api";
import { useCxmFlags } from "@coxauto-ui/core/third-party/launch-darkly";
import KendoEditor from "../wysiwyg-editor/kendo-editor";
import {
  MutableRefObject,
  RefObject,
  useEffect,
  useRef,
  useState
} from "react";
import styled from "styled-components";

const StyledTextAreaWrapper = styled.div<{ $hasError: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  .MuiFormLabel-root {
    color: ${({ $hasError }) => ($hasError ? "#d32f2f" : "")} !important;
  }
  .MuiOutlinedInput-notchedOutline {
    border-color: ${({ $hasError }) => ($hasError ? "#d32f2f" : "")} !important;
  }
`;

export interface BaseMessageInputProps {
  isDisabled: boolean;
  stationeryOptions: StationeryOptions;
  replyText: string;
  setReplyText: (text: string) => void;
  label?: string;
  placeholder?: string;
  showWYSIWYG?: boolean;
  /** External dependencies that should trigger editor height recalculation (e.g., when CC/BCC fields are toggled) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  recalculateDependencies?: any[];
  /**
   * External ref for the Kendo editor instance (email/WYSIWYG path only).
   * Mutually exclusive with textAreaRef — only one is active based on showWYSIWYG.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  editorRef?: RefObject<any>;
  /**
   * External ref for the TextArea element (text/phone path only).
   * Mutually exclusive with editorRef — only one is active based on showWYSIWYG.
   */
  textAreaRef?: RefObject<HTMLTextAreaElement>;
  /** Called when the user edits a media widget title inline (email/WYSIWYG path only). */
  onMediaTitleChange?: (
    endlinkUrl: string,
    mediaId: string,
    newTitle: string
  ) => void;
}

export function BaseMessageInput({
  isDisabled,
  stationeryOptions,
  replyText,
  setReplyText,
  label = "Message",
  placeholder = "",
  showWYSIWYG = false,
  recalculateDependencies = [],
  editorRef,
  textAreaRef,
  onMediaTitleChange
}: BaseMessageInputProps) {
  const { "nx.cxm.show-quill-editor": showQuillEditor } = useCxmFlags();
  const [hasBlurred, setHasBlurred] = useState(false);
  const showError = hasBlurred && !replyText;
  const styledWrapperRef = useRef<HTMLDivElement>(null);

  // Populate textAreaRef with the underlying <textarea> DOM element via querySelector.
  // Interstate's TextArea does not forward its ref to the native <textarea>, so we
  // query for it after mount. Mutually exclusive with editorRef (only active when
  // showWYSIWYG is false).
  // TODO: replace querySelector with a forwarded ref once @interstate/components/TextArea
  // supports ref forwarding (track with the Interstate upgrade ticket).
  useEffect(() => {
    if (textAreaRef && styledWrapperRef.current) {
      const textarea = styledWrapperRef.current.querySelector("textarea");
      if (textarea) {
        (textAreaRef as MutableRefObject<HTMLTextAreaElement | null>).current =
          textarea as HTMLTextAreaElement;
      }
    }
  }, [textAreaRef]);

  const onInputChange = (
    iEvent: InterstateOnChangeEvent<TextInputEventValue>
  ) => {
    const changeVal = iEvent.target.value?.toString();
    if (changeVal === undefined) return;
    setReplyText(changeVal);
  };

  const handleEditorChange = (htmlContent: string) => {
    setReplyText(htmlContent);
  };

  const onBlur = () => {
    setHasBlurred(true);
  };

  if (showWYSIWYG && showQuillEditor) {
    return (
      <KendoEditor
        data-testid="kendo-editor"
        editorRef={editorRef}
        isDisabled={isDisabled}
        recalculateDependencies={recalculateDependencies}
        stationeryOptions={stationeryOptions}
        value={replyText}
        onChange={handleEditorChange}
        onMediaTitleChange={onMediaTitleChange}
      />
    );
  }

  return (
    <StyledTextAreaWrapper $hasError={showError} ref={styledWrapperRef}>
      <TextArea
        autoComplete="off"
        data-testid={replyInputTestId}
        disabled={isDisabled}
        id="conversation-message-input"
        label={`${label} (required)`}
        maxRows={11}
        minRows={4}
        name="ConversationMessageInput"
        placeholder={placeholder}
        value={replyText}
        onBlur={onBlur}
        onChange={onInputChange}
      />
    </StyledTextAreaWrapper>
  );
}
